import React from "react";
import { Button, Placeholder, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCopy } from "@fortawesome/free-solid-svg-icons";
import BillingAlertActionsMenu from "./BillingAlertActionsMenu";
import { formatDate, resolveActionLabel, resolvePriorityTone, safeJoin } from "./billingAlertsUtils";

export default function BillingAlertCard({
  item,
  expanded = false,
  detail = null,
  detailLoading = false,
  detailError = "",
  copied = false,
  onToggle,
  onManage,
  onTake,
  onAssign,
  onRelease,
  onCopyMessage,
}) {
  const detailData = detail || {};
  const actionLabel = resolveActionLabel(item);
  const priorityTone = resolvePriorityTone(item.priority);
  const secondaryLine = safeJoin([
    item.cuit ? `CUIT: ${item.cuit}` : "",
    item.terminal ? `Terminal: ${item.terminal}` : "",
    item.province ? `Provincia: ${item.province}` : "",
    item.phone ? `Teléfono: ${item.phone}` : "",
  ]);
  const tags = detailData.tags?.length ? detailData.tags : item.tags;
  const metricBadges = detailData.metricBadges || [];

  return (
    <article className={`billing-alert-card ${expanded ? "is-expanded" : ""}`}>
      <div className="billing-alert-card__summary">
        <button
          type="button"
          className="billing-alert-card__identity"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <div className="billing-alert-card__main">
            <strong className="billing-alert-card__commerce">{item.commerceName}</strong>
            {secondaryLine ? <div className="billing-alert-card__meta">{secondaryLine}</div> : null}
          </div>
        </button>

        <div className="billing-alert-card__controls" onClick={(event) => event.stopPropagation()}>
          <div className="billing-alert-card__badges">
            <span className="billing-alert-card__pill">{item.status}</span>
            {item.score !== null ? <span className="billing-alert-card__pill">Score {item.score}</span> : null}
            {item.priority ? <span className={`billing-alert-card__priority is-${priorityTone}`}>{item.priority}</span> : null}
          </div>

          <BillingAlertActionsMenu
            item={item}
            onManage={onManage}
            onTake={onTake}
            onAssign={onAssign}
            onRelease={onRelease}
            onCopyMessage={onCopyMessage}
          />

          <button
            type="button"
            className="billing-alert-card__toggle"
            onClick={onToggle}
            aria-label={expanded ? "Contraer alerta de facturación" : "Expandir alerta de facturación"}
            aria-expanded={expanded}
          >
            <FontAwesomeIcon icon={faChevronDown} className={expanded ? "is-open" : ""} />
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="billing-alert-card__detail">
          {detailLoading ? (
            <div className="billing-alert-card__detail-loading">
              <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
              <Placeholder as="div" animation="glow"><Placeholder xs={10} /></Placeholder>
              <Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder>
            </div>
          ) : detailError ? (
            <div className="billing-alert-card__detail-error">
              <span>{detailError}</span>
            </div>
          ) : (
            <>
              <div className="billing-alert-card__detail-column">
                <div className="billing-alert-card__detail-title-row">
                  <h4>{detailData.mainReason || item.mainReason || "Alerta de facturación activa"}</h4>
                  {tags?.length ? (
                    <div className="billing-alert-card__detail-mini-badges">
                      {tags.map((tag) => (
                        <span key={tag} className="billing-alert-card__pill">{tag}</span>
                      ))}
                    </div>
                  ) : null}
                </div>

                {metricBadges.length ? (
                  <div className="billing-alert-card__metrics">
                    {metricBadges.map((metric) => (
                      <span key={metric} className="billing-alert-card__metric-pill">{metric}</span>
                    ))}
                  </div>
                ) : null}

                <div className="billing-alert-card__detail-lines">
                  {detailData.suggestedAction || item.suggestedAction ? (
                    <p><b>Acción sugerida:</b> {detailData.suggestedAction || item.suggestedAction}</p>
                  ) : null}
                  {detailData.pattern || item.pattern ? (
                    <p><b>Patrón detectado:</b> {detailData.pattern || item.pattern}</p>
                  ) : null}
                  {detailData.lastContactAt || item.lastContactAt ? (
                    <p><b>Último contacto:</b> {formatDate(detailData.lastContactAt || item.lastContactAt) || detailData.lastContactAt || item.lastContactAt}</p>
                  ) : null}
                  {detailData.nextContactAt || item.nextContactAt ? (
                    <p><b>Próximo contacto:</b> {formatDate(detailData.nextContactAt || item.nextContactAt) || detailData.nextContactAt || item.nextContactAt}</p>
                  ) : null}
                  {detailData.responsibleName || item.responsibleName ? (
                    <p><b>Responsable:</b> {detailData.responsibleName || item.responsibleName}</p>
                  ) : null}
                  {detailData.score !== null ? <p><b>Score:</b> {detailData.score}</p> : null}
                </div>
              </div>

              <div className="billing-alert-card__detail-column is-message">
                <div className="billing-alert-card__message-label">Mensaje sugerido</div>
                {detailData.suggestedMessage || item.suggestedMessage ? (
                  <>
                    <p className="billing-alert-card__message-text">{detailData.suggestedMessage || item.suggestedMessage}</p>
                    <Button
                      type="button"
                      variant="link"
                      className="billing-alert-card__copy"
                      onClick={() => onCopyMessage(detailData.suggestedMessage || item.suggestedMessage)}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      <span>{copied ? "Mensaje copiado" : "Copiar"}</span>
                    </Button>
                  </>
                ) : (
                  <p className="billing-alert-card__message-empty">Sin mensaje sugerido disponible.</p>
                )}

                <div className="billing-alert-card__detail-action">
                  <Button
                    type="button"
                    className="billing-alert-card__manage-btn"
                    onClick={() => onManage?.(item.raw)}
                    disabled={detailLoading}
                  >
                    {detailLoading ? <Spinner animation="border" size="sm" /> : actionLabel}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}
    </article>
  );
}
